import React from 'react';
import { GraduationCap, ArrowRight, CheckCircle, Play } from 'lucide-react';
import { TestimonialSlider } from './TestimonialSlider';
import { Marquee } from './magicui/Marquee';

import kianFaceImg from '../assets/kian-face-cropped.png';
import kianTeachingImg from '../assets/kian-teaching.png';

export function FounderSpotlight({ onSelectMentor, onOpenVideo }) {
  return (
    <section id="founders" className="py-12 sm:py-16 space-y-12">
      
      {/* KIAN SPOTLIGHT SECTION */}
      <div className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Kian: STEM & Neuroscience Director
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
            McMaster Psychology, Neuroscience & Behaviour Scholar leading 1-on-1 active recall mentorship.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Radial Gold Circle Container for Kian Face */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full border-4 border-amber-400 p-2 shadow-2xl shadow-amber-500/20 bg-slate-900 flex-shrink-0">
              <img
                src={kianFaceImg}
                alt="Kian Ahadi — McMaster Neuroscience Scholar & Founder"
                className="w-full h-full object-cover rounded-full transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>

          {/* Details on Right */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300 text-xs font-semibold">
                <GraduationCap className="h-4 w-4" /> B.Sc. Candidate at McMaster University
              </div>
              <p className="text-slate-700 dark:text-slate-300 text-base sm:text-lg leading-relaxed font-normal">
                Currently pursuing a Bachelor of Science in Psychology, Neuroscience & Behaviour at McMaster University—Canada’s top scientific program—Kian has personally mentored over 35+ high school students to achieve 85%+ averages.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-700 dark:text-slate-300">
              <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center gap-2 font-medium">
                <CheckCircle className="h-4 w-4 text-amber-500" /> Grade 11 & 12 Functions / Calculus
              </div>
              <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center gap-2 font-medium">
                <CheckCircle className="h-4 w-4 text-amber-500" /> Grade 11 & 12 Chemistry Mastery
              </div>
              <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center gap-2 font-medium">
                <CheckCircle className="h-4 w-4 text-amber-500" /> SAT Prep & Essay Architecture
              </div>
              <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center gap-2 font-medium">
                <CheckCircle className="h-4 w-4 text-amber-500" /> Active Recall vs Passive Learning
              </div>
            </div>

            <button
              onClick={() => onSelectMentor('kian')}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 transition cursor-pointer text-left"
            >
              <div>
                <div className="text-sm font-bold text-slate-900 dark:text-white">View Kian's Full Profile</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Syllabi, research background, and direct calendar</div>
              </div>
              <ArrowRight className="h-5 w-5 text-amber-500" />
            </button>
          </div>
        </div>

        {/* Live 1-on-1 Teaching Photo Showcase */}
        <div className="relative rounded-2xl overflow-hidden bg-slate-900 shadow-xl group">
          <img
            src={kianTeachingImg}
            alt="Kian teaching 1-on-1 active recall to student"
            className="w-full h-64 sm:h-80 object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent flex flex-col justify-end p-6 space-y-2">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-amber-400 uppercase tracking-widest">
              <Play className="h-3.5 w-3.5 fill-amber-400" /> In-Person & Live Online Mentorship
            </div>
            <h3 className="text-xl font-bold text-white">Direct 1-on-1 Active Learning Sessions with Kian</h3>
            <p className="text-xs text-slate-300 max-w-xl">
              No recorded video lectures. Every session is live active problem-solving tailored to your high school syllabus.
            </p>
          </div>
        </div>

        {/* Kian's Testimonials Slider */}
        <div className="space-y-4 pt-2">
          <TestimonialSlider onOpenVideo={onOpenVideo} />
        </div>

        {/* Magic UI Marquee for Real University SVG Logo Track */}
        <div className="p-6 rounded-2xl bg-white/80 dark:bg-slate-900/40 space-y-4 shadow-sm dark:shadow-none overflow-hidden">
          <div className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Target University Acceptance Cut-Off Track
          </div>
          <Marquee pauseOnHover className="[--duration:25s]">
            <div className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 shadow-xs">
              <img src="/assets/logos/mcmaster.svg" alt="McMaster logo" className="h-7 w-7 object-contain" />
              <div>
                <div className="font-extrabold text-xs text-slate-900 dark:text-white">McMaster University</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">Health Sciences & Neuroscience</div>
              </div>
            </div>

            <div className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 shadow-xs">
              <img src="/assets/logos/uoft.svg" alt="UofT logo" className="h-7 w-7 object-contain" />
              <div>
                <div className="font-extrabold text-xs text-slate-900 dark:text-white">University of Toronto</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">Life Sciences & Engineering</div>
              </div>
            </div>

            <div className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 shadow-xs">
              <img src="/assets/logos/waterloo.svg" alt="Waterloo logo" className="h-7 w-7 object-contain" />
              <div>
                <div className="font-extrabold text-xs text-slate-900 dark:text-white">University of Waterloo</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">Mathematics & Science</div>
              </div>
            </div>

            <div className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 shadow-xs">
              <img src="/assets/logos/mcgill.svg" alt="McGill logo" className="h-7 w-7 object-contain" />
              <div>
                <div className="font-extrabold text-xs text-slate-900 dark:text-white">McGill University</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">Pre-Med & Science</div>
              </div>
            </div>

            <div className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 shadow-xs">
              <img src="/assets/logos/ubc.svg" alt="UBC logo" className="h-7 w-7 object-contain" />
              <div>
                <div className="font-extrabold text-xs text-slate-900 dark:text-white">University of British Columbia</div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">Science & Applied Science</div>
              </div>
            </div>
          </Marquee>
        </div>
      </div>

    </section>
  );
}
